// @flow
import React, { useRef, useEffect } from 'react'
import debounce from 'lodash/debounce'
import isInViewport from '@utils/is-in-viewport'

// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop() {}

interface Props {
  children: React.ReactNode
  onEnter?: (e: number) => void
  onExit?: (e: number) => void
  onProgress?: (e: number) => void
}

const ScrollTrigger = ({
  children,
  onEnter = noop,
  onExit = noop,
  onProgress = noop,
}: Props) => {
  const trigRef = useRef<HTMLSpanElement | null>(null)
  const inViewport = useRef(false)

  useEffect(() => {
    const checkPosition = debounce(() => {
      if (!trigRef.current) {
        return
      }
      const isIn = isInViewport(trigRef.current)
      const viewportEnd = Math.max(
        document.documentElement.clientHeight,
        window.innerHeight || 0,
      )
      const distance = trigRef.current.getBoundingClientRect()
      const progress = Math.max(
        0,
        Math.min(1, 1 - distance.bottom / (viewportEnd + distance.height)),
      )
      onProgress(progress)

      if (inViewport.current === isIn) {
        return
      }

      if (isIn) {
        onEnter(progress)
      }
      else {
        onExit(progress)
      }
      inViewport.current = isIn
    })

    window.addEventListener('touchmove', checkPosition, false)
    window.addEventListener('scroll', checkPosition, false)
    window.addEventListener('resize', checkPosition, false)

    checkPosition()

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('touchmove', checkPosition)
      window.removeEventListener('scroll', checkPosition)
      window.removeEventListener('resize', checkPosition)
    }
  }, [])

  return <span ref={trigRef}>{children}</span>
}

export default ScrollTrigger
